import { Icone, type PropsDeIcone } from './IconesDeNavegacao'

/**
 * Os ícones de ação e de estado da interface. Ponto de entrada único do conjunto: reexporta
 * também os do menu, que moram em `IconesDeNavegacao.tsx` por causa do teto de 200 linhas.
 *
 * Sem biblioteca de ícones, de propósito. Um pacote traz milhares de desenhos para usar vinte
 * e três, e a aplicação roda atrás de CSP estrita, onde o que é carregado de fora simplesmente
 * não chega. Alguns `path` custam menos que a dependência.
 */

export type { PropsDeIcone } from './IconesDeNavegacao'
export {
  Icone,
  IconeCalendario,
  IconeChamado,
  IconeComite,
  IconeConfiguracao,
  IconeFechar,
  IconeIndicador,
  IconeMenu,
  IconeMudanca,
  IconeNovo,
  IconeOrdemServico,
  IconePainel,
  IconeQuadro,
  IconeRelatorio,
  IconeSair,
  IconeUsuarios,
} from './IconesDeNavegacao'

/** Ajuda: a interrogação que abre a explicação de um termo. */
export function IconeAjuda(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.6 9.3a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.7-.9 1.3v.5" />
      <path d="M12 17h.01" />
    </Icone>
  )
}

/** Busca por texto. */
export function IconeBusca(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M20 20l-4.2-4.2" />
    </Icone>
  )
}

/** Filtro: o funil. */
export function IconeFiltro(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M4 5h16l-6.2 7.4V19l-3.6-2v-4.6L4 5Z" />
    </Icone>
  )
}

/** Alerta: impedimento, pendência do plano, aviso. */
export function IconeAlerta(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M12 4.5 21 20H3l9-15.5Z" />
      <path d="M12 10v4" />
      <path d="M12 17.2h.01" />
    </Icone>
  )
}

/** Cadeado: bloqueada para produção enquanto o comitê não aprova. */
export function IconeCadeado(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <rect x="4.5" y="10" width="15" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </Icone>
  )
}

/** Histórico: o relógio com a seta para trás. */
export function IconeHistorico(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.7-6.2" />
      <path d="M3 3v5h5" />
      <path d="M12 8v4.4l3 1.8" />
    </Icone>
  )
}

/** Imprimir ou salvar em PDF. */
export function IconeImprimir(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M7 9V4h10v5" />
      <path d="M7 18H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
      <rect x="7" y="15" width="10" height="6" rx="1" />
    </Icone>
  )
}

/**
 * Seta para a direita.
 *
 * Uma só, girada por classe (`rotate-180`, `rotate-90`) quando aponta para outro lado —
 * quatro desenhos quase iguais divergem no primeiro retoque.
 */
export function IconeSeta(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M9 5l7 7-7 7" />
    </Icone>
  )
}

/** Confirmação. */
export function IconeCheque(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M5 12.5l4.5 4.5L19 7" />
    </Icone>
  )
}

/** Repositório de código: o grafo de commits, com dois ramos se juntando. */
export function IconeRepositorio(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="6" cy="18" r="2.5" />
      <circle cx="18" cy="9" r="2.5" />
      <path d="M6 8.5v7M18 11.5c0 3-3 4-6 4.5s-5 1.5-6 2" />
    </Icone>
  )
}

/** Carteira: o crédito de IA — saldo declarado pela administração. */
export function IconeCarteira(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M3 7a2 2 0 0 1 2-2h13a1 1 0 0 1 1 1v2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1v-3" />
      <path d="M16 12h5v4h-5a2 2 0 0 1 0-4Z" />
    </Icone>
  )
}

/** Faísca: a leitura por inteligência artificial. */
export function IconeIa(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8Z" />
      <path d="M18 16l.8 2.2L21 19l-2.2.8L18 22l-.8-2.2L15 19l2.2-.8Z" />
    </Icone>
  )
}

/** Lote: várias linhas de uma vez, com uma seta — a análise de um mês inteiro. */
export function IconeLote(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M4 6h9M4 12h9M4 18h9" />
      <path d="M17 9l3 3-3 3M20 12h-4" />
    </Icone>
  )
}

/** Balança: pontos administrativos — a pontuação que vira desconto. */
export function IconeBalanca(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <path d="M12 3v18M6 21h12M12 6l6 2.5M12 6L6 8.5" />
      <path d="M3 14l3-5.5L9 14a3 3 0 0 1-6 0Z" />
      <path d="M15 14l3-5.5 3 5.5a3 3 0 0 1-6 0Z" />
    </Icone>
  )
}

/** Uma pessoa só: a própria conta. Duas pessoas é o cadastro. */
export function IconeConta(props: PropsDeIcone) {
  return (
    <Icone {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </Icone>
  )
}

